import React, { useMemo, useState } from 'react';
import { ArrowUpDown, Euro, CalendarDays, Users as UsersIcon, Receipt } from 'lucide-react';

import { useEvents } from '@/contexts/EventsContext';
import { useUsers } from '@/contexts/UsersContext';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import StaffPerformanceDetailModal, { StaffPerfTarget } from './StaffPerformanceDetailModal';

const euro = (n: number) => `€ ${Math.round(n).toLocaleString('it-IT')}`;

type SortKey = 'name' | 'revenue' | 'bookings' | 'clients' | 'ticket';

interface Row {
    id: string;
    name: string;
    initials: string;
    revenue: number;
    bookings: number;
    clients: number;
    ticket: number;
}

interface StaffPerformanceOverviewProps {
    /** Periodo selezionato, gestito dall'header della pagina Dipendenti. */
    period: string;
}

/**
 * Panoramica economica per dipendente (owner-only).
 * Produzione, appuntamenti, clienti serviti e scontrino medio su periodo selezionabile, ordinabile.
 */
const StaffPerformanceOverview: React.FC<StaffPerformanceOverviewProps> = ({ period }) => {
    const { bookings } = useEvents();
    const { getValidBookings, calculateRevenue, filterDataByPeriod } = useAnalytics();
    const { users } = useUsers();

    const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'revenue', dir: 'desc' });
    const [selected, setSelected] = useState<StaffPerfTarget | null>(null);

    const rows: Row[] = useMemo(() => {
        const activeUsers = (users || []).filter((u: any) => u.is_active !== false);
        const valid = getValidBookings(bookings || []);
        const periodBookings = filterDataByPeriod(valid, period, 'bookings');

        return activeUsers.map((u: any): Row => {
            const own = periodBookings.filter((b: any) => b.hairdresser_id === u.id);
            const revenue = calculateRevenue(own);
            const clients = new Set(own.map((b: any) => b.client_id).filter(Boolean)).size;
            const first = u.first_name || '';
            const last = u.last_name || '';
            return {
                id: String(u.id),
                name: `${first} ${last}`.trim() || u.email || '—',
                initials: `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '?',
                revenue,
                bookings: own.length,
                clients,
                ticket: own.length > 0 ? revenue / own.length : 0,
            };
        });
    }, [users, bookings, period, getValidBookings, calculateRevenue, filterDataByPeriod]);

    const sortedRows = useMemo(() => {
        const copy = [...rows];
        copy.sort((a, b) => {
            let cmp: number;
            if (sort.key === 'name') cmp = a.name.localeCompare(b.name);
            else cmp = (a[sort.key] as number) - (b[sort.key] as number);
            return sort.dir === 'asc' ? cmp : -cmp;
        });
        return copy;
    }, [rows, sort]);

    const toggleSort = (key: SortKey) => {
        setSort((prev) =>
            prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'name' ? 'asc' : 'desc' },
        );
    };

    const columns: { key: SortKey; label: string; icon?: any; align: 'left' | 'right' }[] = [
        { key: 'name', label: 'Dipendente', align: 'left' },
        { key: 'revenue', label: 'Produzione', icon: Euro, align: 'right' },
        { key: 'bookings', label: 'Appuntamenti', icon: CalendarDays, align: 'right' },
        { key: 'clients', label: 'Clienti serviti', icon: UsersIcon, align: 'right' },
        { key: 'ticket', label: 'Scontrino medio', icon: Receipt, align: 'right' },
    ];

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-100 bg-gray-50/50">
                                {columns.map((col) => (
                                    <th
                                        key={col.key}
                                        onClick={() => toggleSort(col.key)}
                                        className={`px-4 py-3 font-bold text-gray-500 uppercase tracking-wider text-xs cursor-pointer select-none hover:text-gray-900 transition-colors ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                                    >
                                        <span className={`inline-flex items-center gap-1.5 ${col.align === 'right' ? 'flex-row-reverse' : ''}`}>
                                            {col.label}
                                            <ArrowUpDown size={12} className={sort.key === col.key ? 'text-clip-500' : 'text-gray-300'} />
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="px-4 py-10 text-center text-gray-400">
                                        Nessun dipendente attivo.
                                    </td>
                                </tr>
                            ) : (
                                sortedRows.map((r) => (
                                    <tr
                                        key={r.id}
                                        onClick={() => setSelected({ id: r.id, name: r.name, initials: r.initials })}
                                        className="border-b border-gray-50 last:border-0 hover:bg-clip-50/40 transition-colors cursor-pointer"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl bg-clip-100 text-clip-600 flex items-center justify-center text-xs font-bold shrink-0">
                                                    {r.initials}
                                                </div>
                                                <span className="font-medium text-gray-900">{r.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right font-bold text-gray-900">{euro(r.revenue)}</td>
                                        <td className="px-4 py-3 text-right text-gray-700">{r.bookings}</td>
                                        <td className="px-4 py-3 text-right text-gray-700">{r.clients}</td>
                                        <td className="px-4 py-3 text-right text-gray-700">{euro(r.ticket)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <StaffPerformanceDetailModal
                hairdresser={selected}
                isOpen={!!selected}
                onClose={() => setSelected(null)}
            />
        </div>
    );
};

export default StaffPerformanceOverview;
